import { useMemo } from 'react';

import {
  Alert01Icon,
  ArrowReloadHorizontalIcon,
  Bookmark02Icon,
  Cancel01Icon,
  Undo02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslation } from 'react-i18next';

import RolloverTooltip from 'components/RolloverTooltip/RolloverTooltip';
import { useTableContext } from 'components/Table/Table';

import styles from './FiltersChangedBanner.module.scss';
import { SavedViewDefaultToggle } from './SavedViewDefaultToggle';
import {
  TRANSIENT_TABLE_PARAM_KEYS,
  buildReportingFilterPillsFromQueryParams,
  paramObjectHasActiveFilter,
  savedViewFilterSignature,
} from './savedViewQueryParams';

export const ActiveViewBanner = ({
  viewName,
  activeViewData,
  /** When false, do not show the "filters changed" / "all cleared" branch until the table has applied the saved view (avoids flash on tab remount). */
  bannerViewQuerySynced = true,
  onClearView,
  onRestore,
  onUpdate,
  onSaveNew,
  onSetDefault,
}) => {
  const { t } = useTranslation();
  const { queryParams, replaceAndApply } = useTableContext();

  const hasChanged = useMemo(() => {
    if (!activeViewData?.savedQueryParams) return false;
    return (
      savedViewFilterSignature(queryParams) !==
      savedViewFilterSignature(activeViewData.savedQueryParams)
    );
  }, [queryParams, activeViewData]);

  const hasNoFilters = useMemo(
    () =>
      !Object.entries(queryParams)
        .filter(([k]) => !TRANSIENT_TABLE_PARAM_KEYS.includes(k))
        .some(([, param]) => paramObjectHasActiveFilter(param)),
    [queryParams],
  );

  const handleRestore = () => {
    replaceAndApply(activeViewData.savedQueryParams ?? {});
    onRestore?.();
  };

  const handleClearView = () => {
    replaceAndApply({});
    onClearView?.();
  };

  const currentFilterPills = useMemo(
    () => buildReportingFilterPillsFromQueryParams(queryParams),
    [queryParams],
  );

  const viewNameButton = <span className={styles.bannerViewName}>{viewName}</span>;

  const defaultToggleButton = (
    <SavedViewDefaultToggle
      viewId={activeViewData?.id}
      isDefault={!!activeViewData?.isDefault}
      onSetDefault={onSetDefault}
    />
  );

  if (hasChanged && bannerViewQuerySynced) {
    return (
      <div className={styles.activeSavedViewBanner}>
        <div className={styles.bannerLeft}>
          <div className={styles.bannerIconSquare}>
            <HugeiconsIcon icon={Alert01Icon} size={20} strokeWidth={2} />
          </div>
          <div className={styles.bannerTextCol}>
            <p className={styles.bannerDesc}>
              {hasNoFilters ? (
                <>
                  {t('reporting.allFiltersClearedDesc1')}
                  <strong>{`"${viewName}"`}</strong>
                  {t('reporting.allFiltersClearedDesc2')}
                </>
              ) : (
                <>
                  {t('reporting.filtersChangedDesc1')}
                  <strong>{`"${viewName}"`}</strong>
                  {t('reporting.filtersChangedDesc2')}
                </>
              )}
            </p>
            {viewNameButton}
          </div>
        </div>
        <div className={styles.bannerIconActions}>
          <RolloverTooltip tooltip={t('reporting.restore')}>
            <button className={styles.bannerCloseBtn} onClick={handleRestore}>
              <HugeiconsIcon icon={Undo02Icon} size={14} strokeWidth={2.5} />
            </button>
          </RolloverTooltip>
          {!hasNoFilters && (
            <RolloverTooltip tooltip={t('reporting.updateView')}>
              <button
                className={styles.bannerCloseBtn}
                onClick={() => onUpdate?.(currentFilterPills, queryParams)}>
                <HugeiconsIcon icon={ArrowReloadHorizontalIcon} size={14} strokeWidth={2.5} />
              </button>
            </RolloverTooltip>
          )}
          {!hasNoFilters && (
            <RolloverTooltip tooltip={t('reporting.saveNewView')}>
              <button className={styles.bannerCloseBtn} onClick={onSaveNew}>
                <HugeiconsIcon icon={Bookmark02Icon} size={14} strokeWidth={2.5} />
              </button>
            </RolloverTooltip>
          )}
          {defaultToggleButton}
          <RolloverTooltip tooltip={t('reporting.clearView')}>
            <button className={styles.bannerCloseBtn} onClick={handleClearView}>
              <HugeiconsIcon icon={Cancel01Icon} size={14} strokeWidth={2.5} />
            </button>
          </RolloverTooltip>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.activeSavedViewBanner}>
      <div className={styles.bannerLeft}>
        <div className={styles.bannerIconSquare}>
          <HugeiconsIcon icon={Bookmark02Icon} size={20} strokeWidth={2} />
        </div>
        <div className={styles.bannerTextCol}>
          <p className={styles.bannerLabel}>{t('reporting.activeSavedView')}</p>
          {viewNameButton}
        </div>
      </div>
      <div className={styles.bannerIconActions}>
        {defaultToggleButton}
        <RolloverTooltip tooltip={t('reporting.clearView')}>
          <button className={styles.bannerCloseBtn} onClick={handleClearView}>
            <HugeiconsIcon icon={Cancel01Icon} size={14} strokeWidth={2.5} />
          </button>
        </RolloverTooltip>
      </div>
    </div>
  );
};
